import { useRouter } from 'next/router'
import { stripHtml } from '@/utils/htmlUtils'
import { BRAND_NAME, BRAND_ALTERNATE_NAME } from '@/utils/constants'

/**
 * Builds a general purpose WebPage JSON-LD block for any CMS driven page.
 * Falls back gracefully when specific SEO fields are missing.
 */
const WebPageSD = ({ data }) => {
  const router = useRouter()
  if (!data) return null

  const domain =
    process.env.NEXT_PUBLIC_DOMAIN || 'https://sitkaseafoodmarket.com'
  const url = `${domain}${router.asPath}`

  const seo = data?.seo || data?.fields?.seo || {}
  const hero = data?.hero || data?.fields?.hero || {}
  const content = data?.content || data?.fields?.content || []

  const title =
    seo?.metaTitle ||
    data?.title ||
    data?.fields?.title ||
    BRAND_NAME ||
    ''
  const descriptionCandidate =
    seo?.metaDesc ||
    data?.description ||
    data?.fields?.description ||
    ''
  const description = stripHtml(descriptionCandidate)?.trim() || ''

  const imageCandidates = [
    seo?.shareGraphic?.asset?.url,
    hero?.desktopBackgroundImage?.asset?.url,
    hero?.image?.asset?.url,
    hero?.backgroundImage?.asset?.url,
  ]

  if (Array.isArray(content)) {
    for (const section of content) {
      if (section?.image?.asset?.url) {
        imageCandidates.push(section.image.asset.url)
      } else if (section?.desktopBackgroundImage?.asset?.url) {
        imageCandidates.push(section.desktopBackgroundImage.asset.url)
      } else if (section?.mobileBackgroundImage?.asset?.url) {
        imageCandidates.push(section.mobileBackgroundImage.asset.url)
      }
    }
  }

  const primaryImage = imageCandidates.find(Boolean)

  const datePublished =
    data?.publishedDate ||
    data?.fields?.publishedDate ||
    data?.createdAt ||
    data?._createdAt ||
    data?.fields?._createdAt ||
    undefined
  const dateModified =
    data?.updatedAt ||
    data?._updatedAt ||
    data?.fields?.updatedAt ||
    data?.fields?._updatedAt ||
    undefined

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${domain}/#website`,
      url: domain,
      name: BRAND_NAME,
      alternateName: BRAND_ALTERNATE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: BRAND_NAME,
      url: domain,
    },
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [url],
      },
    ],
  }

  if (primaryImage) {
    schema.primaryImageOfPage = {
      '@type': 'ImageObject',
      url: primaryImage,
    }
  }

  if (datePublished) {
    schema.datePublished = new Date(datePublished).toISOString()
  }

  if (dateModified) {
    schema.dateModified = new Date(dateModified).toISOString()
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}

export default WebPageSD
